/**
 * Pure logic for the live-update health monitor.
 *
 * Context: the app has no server push — the visit list and visit status colours
 * are kept current purely by react-query interval polling. A reported (but not
 * lab-reproducible) bug freezes auto-updates on a few mobile PWA devices while
 * manual writes still succeed. The leading — unconfirmed — hypothesis is that
 * react-query's interval polling pauses when the document is backgrounded and
 * never resumes on some devices (the `visibilitychange`/focus signal is dropped),
 * so `focusManager.isFocused()` stays false even while the page is visible.
 *
 * This module turns a sampled state snapshot into a stall verdict. It is kept
 * pure (no react-query / DOM access) so it can be unit-tested and so the
 * decision rules are explicit and reviewable. The hook in
 * `useUpdateHealthMonitor.ts` collects the snapshot and reports verdicts; it
 * deliberately takes NO recovery action, so it cannot mask the bug while we
 * gather evidence in production.
 */

export type UpdateHealthSnapshot = {
  /** Epoch ms when the snapshot was taken. */
  ts: number
  /** `document.visibilityState` — 'visible' | 'hidden' | other. */
  visibilityState: string
  /** `document.hasFocus()` — note: false on installed PWAs even when visible. */
  documentHasFocus: boolean
  /** `navigator.onLine`. */
  navigatorOnLine: boolean
  /** react-query `focusManager.isFocused()` — drives interval resume + focus refetch. */
  reactQueryFocused: boolean
  /** react-query `onlineManager.isOnline()`. */
  reactQueryOnline: boolean
  /** Number of active (observed) ServiceVisit "by source" list queries. */
  activeVisitQueries: number
  /** Age in ms of the freshest visit-list data (now - newest dataUpdatedAt), or null if no active query. */
  visitDataAgeMs: number | null
  /** Queries currently in fetchStatus 'paused' (the direct smoking-gun signal). */
  pausedQueryCount: number
  /** Queries in error state. */
  erroredQueryCount: number
  /** Queries currently fetching. */
  fetchingQueryCount: number
  /**
   * Whether the Network Information API was present at all. It is UNAVAILABLE on
   * iOS / Safari (incl. iOS PWAs) for privacy reasons, so on those devices every
   * connection* field below is null/false and must be read as "unknown", NOT as
   * "good link". Only meaningful where this is true (Chromium/Android).
   */
  connectionInfoAvailable: boolean
  /** navigator.connection.effectiveType ('slow-2g'|'2g'|'3g'|'4g'), or null if unavailable. */
  connectionEffectiveType: string | null
  /** navigator.connection.downlink in Mbps, or null if unavailable. */
  connectionDownlinkMbps: number | null
  /** navigator.connection.rtt in ms, or null if unavailable. */
  connectionRttMs: number | null
  /** navigator.connection.saveData — user has data-saver on. False/unknown on iOS. */
  connectionSaveData: boolean
  /**
   * `document.wasDiscarded` — the tab was discarded under memory pressure and
   * reloaded. Common on low-RAM Android. Chromium-only (null elsewhere).
   */
  wasDiscarded: boolean | null
  /** navigator.deviceMemory — approximate device RAM in GiB (low values => cheap device). Chromium-only. */
  deviceMemoryGb: number | null
  /** navigator.hardwareConcurrency — logical CPU cores. */
  hardwareConcurrency: number | null
}

/** Event types after which the page may have become visible again. */
export const RESUME_EVENT_TYPES = [
  'visibilitychange',
  'focus',
  'pageshow',
  'resume',
]

export type ResumeTracking = {
  /** Epoch ms when the page last went hidden, or 0 when (again) visible. */
  hiddenAt: number
  /** Measured time the page was away on the most recent resume. */
  awayMs: number
  /** Epoch ms of the most recent resume (became-visible) event. */
  resumeAt: number
}

export const initialResumeTracking: ResumeTracking = {
  hiddenAt: 0,
  awayMs: 0,
  resumeAt: 0,
}

/**
 * Fold a lifecycle event into the resume-tracking state.
 *
 * Away-time is measured from when the page WENT HIDDEN, not from the previous
 * visible moment, so the `visibilitychange`+`focus`/`resume` pair that fires
 * together on a single resume cannot clobber it to ~0 (the second event would
 * otherwise measure only the few ms since the first). It is computed exactly
 * once per hidden→visible transition; subsequent paired resume events preserve
 * it.
 */
export const trackResumeLifecycle = (
  prev: ResumeTracking,
  eventType: string,
  visibilityState: string,
  now: number
): ResumeTracking => {
  const isVisible = visibilityState === 'visible'
  if (isVisible && RESUME_EVENT_TYPES.includes(eventType)) {
    // First visible event of the resume computes away-time; later paired ones
    // (hiddenAt already cleared) keep the value instead of recomputing ~0.
    const awayMs = prev.hiddenAt > 0 ? now - prev.hiddenAt : prev.awayMs
    return { hiddenAt: 0, awayMs, resumeAt: now }
  }
  if (!isVisible && prev.hiddenAt === 0) {
    // First hidden event of a sleep; ignore later hidden events so the away
    // window is measured from when we actually went away.
    return { ...prev, hiddenAt: now }
  }
  return prev
}

/** Measured downlink at or below this (Mbps) is treated as constrained. */
const LOW_DOWNLINK_MBPS = 0.5
/** Measured round-trip at or above this (ms) is treated as constrained. */
const HIGH_RTT_MS = 2000

/**
 * True when the link looks bandwidth-constrained. Used to back off telemetry
 * frequency so diagnostics do not compete with the app for scarce bandwidth.
 *
 * IMPORTANT: this must work on iOS, where the Network Information API does not
 * exist. So the cross-platform signal is `navigator.onLine === false` (which iOS
 * does support); the Network Information fields are an additional, best-effort
 * signal only where available (Android/Chromium). When the API is absent and the
 * device reports online, we simply cannot tell — and deliberately do not assume
 * either way (telemetry's own footprint is tiny: ≤1 small request/min).
 *
 * `effectiveType` is NOT trusted on its own: field reports show devices on a
 * ~0.15 Mbps link still reporting `effectiveType: '4g'`. So we also treat a low
 * measured `downlink` or a high `rtt` as constrained, independent of the bucket.
 */
export const isConstrainedConnection = (
  snapshot: UpdateHealthSnapshot
): boolean => {
  if (!snapshot.navigatorOnLine) {
    return true
  }
  if (!snapshot.connectionInfoAvailable) {
    return false
  }
  return (
    snapshot.connectionSaveData ||
    snapshot.connectionEffectiveType === 'slow-2g' ||
    snapshot.connectionEffectiveType === '2g' ||
    (snapshot.connectionDownlinkMbps !== null &&
      snapshot.connectionDownlinkMbps <= LOW_DOWNLINK_MBPS) ||
    (snapshot.connectionRttMs !== null &&
      snapshot.connectionRttMs >= HIGH_RTT_MS)
  )
}

export type StallThresholds = {
  /** Visit data older than this (ms) while visible counts as stale. */
  staleMs: number
}

export const defaultStallThresholds: StallThresholds = {
  staleMs: 30_000,
}

export type StallEvaluation = {
  /** True when the user-visible symptom condition holds (visible + stale/paused). */
  stalled: boolean
  /** All diagnostic conditions that apply — includes the "why" signals, not just the symptom. */
  reasons: string[]
}

/**
 * Decide whether the snapshot represents a stalled auto-update situation.
 *
 * We only consider the page "stalled" when it is VISIBLE — a hidden tab is
 * expected to have paused polling. The verdict fires on the user-visible
 * symptom (stale visit data or paused queries); the additional reasons
 * (`react-query-unfocused-while-visible`, offline mismatches) are the
 * discriminators that let us confirm or reject the backgrounding hypothesis
 * from the reported telemetry.
 */
export const evaluateStall = (
  snapshot: UpdateHealthSnapshot,
  thresholds: StallThresholds = defaultStallThresholds
): StallEvaluation => {
  const reasons: string[] = []

  const isVisible = snapshot.visibilityState === 'visible'
  if (!isVisible) {
    return { stalled: false, reasons }
  }

  const dataStale =
    snapshot.visitDataAgeMs !== null &&
    snapshot.visitDataAgeMs > thresholds.staleMs
  if (dataStale) {
    reasons.push('visit-data-stale')
  }
  if (snapshot.pausedQueryCount > 0) {
    reasons.push('queries-paused')
  }

  // Discriminators for the leading hypothesis (visible but react-query thinks
  // it is not focused/online → interval polling will not resume).
  if (!snapshot.reactQueryFocused) {
    reasons.push('react-query-unfocused-while-visible')
  }
  if (!snapshot.reactQueryOnline) {
    reasons.push('react-query-offline')
  }
  if (!snapshot.navigatorOnLine) {
    reasons.push('navigator-offline')
  }

  const stalled = dataStale || snapshot.pausedQueryCount > 0
  return { stalled, reasons }
}

/**
 * After the page resumes from the background, visit data is necessarily stale
 * until the refetch storm lands, so a stall observed within the resume grace
 * window is expected — not a bug — and would otherwise produce a false-positive
 * on every long resume. Returns true when a stall should be suppressed as such
 * transient post-resume staleness.
 *
 * Suppressed when stale visit data is present and no genuinely-bad signal is:
 * `queries-paused` (a real stuck fetch) is never suppressed. Transient
 * discriminators expected to be settling right after a resume (notably
 * `react-query-unfocused-while-visible`) may co-occur and do NOT block
 * suppression — if they persist, the stall outlives the grace window and
 * reports normally, which is exactly the "recovery genuinely failed" case.
 */
export const isTransientResumeStall = (
  evaluation: StallEvaluation,
  msSinceResume: number,
  graceMs: number
): boolean => {
  if (msSinceResume >= graceMs) {
    return false
  }
  const reasons = evaluation.reasons
  return (
    reasons.includes('visit-data-stale') && !reasons.includes('queries-paused')
  )
}

export type ReportSeverity = 'error' | 'warning'

/**
 * Severity of a stall report, so the genuinely-stuck signatures can be
 * alerted on separately from softer warnings.
 *
 * `error` (the cases worth paging on):
 *  - `recoveryFailed` — a stall that survived the post-resume grace window
 *    (auto-update genuinely did not recover after the device woke);
 *  - `queries-paused` — a query stuck in fetchStatus 'paused'.
 * Everything else (e.g. plain stale data) stays `warning`.
 */
export const severityFor = (
  evaluation: StallEvaluation,
  recoveryFailed: boolean
): ReportSeverity =>
  recoveryFailed || evaluation.reasons.includes('queries-paused')
    ? 'error'
    : 'warning'

/**
 * Compact, single-line serialisation for the server log message (which is a
 * plain string). Prefixed with a stable token so it is greppable/alertable in
 * GlitchTip. `extra` merges additional context (e.g. how long the page was away
 * when a stall is reported shortly after a resume).
 */
export const formatHealthLog = (
  snapshot: UpdateHealthSnapshot,
  evaluation: StallEvaluation,
  extra?: Record<string, unknown>
): string =>
  `[update-health] ${evaluation.reasons.join(',') || 'ok'} ` +
  JSON.stringify({
    vis: snapshot.visibilityState,
    hasFocus: snapshot.documentHasFocus,
    navOnline: snapshot.navigatorOnLine,
    rqFocused: snapshot.reactQueryFocused,
    rqOnline: snapshot.reactQueryOnline,
    activeVisitQueries: snapshot.activeVisitQueries,
    visitDataAgeMs: snapshot.visitDataAgeMs,
    paused: snapshot.pausedQueryCount,
    errored: snapshot.erroredQueryCount,
    fetching: snapshot.fetchingQueryCount,
    netInfo: snapshot.connectionInfoAvailable, // false on iOS — fields below are "unknown", not "good"
    netType: snapshot.connectionEffectiveType,
    netDownMbps: snapshot.connectionDownlinkMbps,
    netRttMs: snapshot.connectionRttMs,
    saveData: snapshot.connectionSaveData,
    wasDiscarded: snapshot.wasDiscarded,
    deviceMemoryGb: snapshot.deviceMemoryGb,
    cores: snapshot.hardwareConcurrency,
    ...extra,
  })

/**
 * Throttle decision for a report. Errors (confirmed-stuck, alertable) are
 * throttled on a separate clock from warnings, so a recent warning can never
 * suppress the first error report — the severity must be computed BEFORE this
 * gate, not after.
 */
export const shouldEmitReport = (
  severity: ReportSeverity,
  now: number,
  lastWarnAt: number,
  lastErrorAt: number,
  throttleMs: number
): boolean =>
  now - (severity === 'error' ? lastErrorAt : lastWarnAt) >= throttleMs
